import { evaluateEntitlement } from '@tabbio/utils';
import { usePathname } from 'expo-router';
import type { EntitlementDecision } from '@tabbio/types';
import { applyPaywallBypass } from '../lib/dev-paywall-bypass';
import { useSubscriptionState } from './useSubscriptionState';
import { useEntitlement } from '../providers/useSubscriptionState';
import { useUpgradeModal } from '../providers/UpgradeModalProvider';
import { createUpgradeContext } from '../lib/monetization-context';

/**
 * Metering key for the in-editor "Spark" enhance (CV text enhancement).
 * Matches the server-side AIActionKey used by `POST /api/onboarding/ai/enhance-text`.
 */
export const SPARK_ENHANCE_KEY = 'onboarding.text_enhance' as const;

/**
 * Thrown by {@link useSparkEnhance}.ensureAllowed when the user is at their tier
 * limit. The upgrade modal has already been opened by the time this is thrown -
 * callers should swallow it (no error toast) and simply stop.
 */
export class SparkEnhanceEntitlementError extends Error {
  readonly reasonCode: string;
  constructor(message: string, reasonCode: string) {
    super(message);
    this.name = 'SparkEnhanceEntitlementError';
    this.reasonCode = reasonCode;
  }
}

export interface SparkEnsureAllowedOptions {
  complexity?: 'standard' | 'complex';
}

/**
 * Client-side entitlement guard for the Spark enhance action, modelled on
 * `useCvSectionPreview`. Call `ensureAllowed()` immediately before invoking the
 * enhance mutation: it evaluates the live entitlement (honouring the dev paywall
 * bypass) and, when blocked, opens the upgrade modal and throws
 * `SparkEnhanceEntitlementError` instead of letting the request fail with a raw
 * server error. When allowed it optimistically decrements credits; pair it with
 * `reconcile()` in a `finally` to re-sync the real balance afterwards.
 *
 * When no subscription snapshot is available it returns `null` (gate skipped)
 * rather than blocking: see the note in `ensureAllowed`.
 */
export function useSparkEnhance() {
  const { catalog, snapshot, beginOptimisticAction, invalidateSubscription } =
    useSubscriptionState();
  const upgradeModal = useUpgradeModal();
  const pathname = usePathname();

  // Live decision for optional display (e.g. remaining credits). The
  // authoritative gate is re-evaluated against a fresh snapshot in ensureAllowed.
  const entitlement = useEntitlement(SPARK_ENHANCE_KEY, { subject: 'ai_action' });

  const ensureAllowed = async (
    options?: SparkEnsureAllowedOptions,
  ): Promise<EntitlementDecision | null> => {
    // The client gate is only an optimization: it evaluates the live snapshot to
    // open the upgrade modal early and decrement credits optimistically. When no
    // snapshot exists it cannot evaluate: the norm during onboarding, where the
    // subscription query is intentionally disabled until onboarding completes
    // (SubscriptionProvider `enabled`), so `snapshot` stays null for the whole
    // flow (and briefly on first mount elsewhere). Rather than block the user
    // with a hard "Subscription state is loading" error (issue #83), skip the
    // client gate and let the request proceed; the server is the source of truth
    // for metering and enforces the real limit: matching the native forms,
    // which never gate.
    if (!snapshot) {
      return null;
    }

    const decision = applyPaywallBypass(
      evaluateEntitlement(snapshot, SPARK_ENHANCE_KEY, catalog, {
        subject: 'ai_action',
        complexity: options?.complexity ?? 'standard',
      }),
    );

    if (!decision.allowed) {
      upgradeModal.open(
        createUpgradeContext({
          decision,
          placement: 'studio_generate',
          entryPoint: 'cv.spark_enhance',
          returnTo: pathname,
        }),
      );
      throw new SparkEnhanceEntitlementError(
        decision.message ?? 'Upgrade required to use Spark enhance.',
        decision.reasonCode,
      );
    }

    beginOptimisticAction(decision.estimatedCredits ?? 1);
    return decision;
  };

  const reconcile = () => invalidateSubscription();

  return {
    ensureAllowed,
    reconcile,
    entitlement,
    remainingCredits:
      entitlement.remainingCreditsThisCycle ??
      entitlement.remainingCreditsToday ??
      null,
  };
}
